import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/pricing", label: "Pricing", id: "pricing" },
  { to: "/funding", label: "Funding", id: "funding" },
  { to: "/testimonials", label: "Testimonials", id: "testimonials" },
  { to: "/about", label: "About", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <img src="/logo.png" alt="Clear Path Repair" className="h-10 w-auto object-contain" />
      <div className="leading-none">
        <span className="block font-serif text-lg tracking-tight text-white">Clear Path</span>
        <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Repair</span>
      </div>
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50 bg-[#08090C]/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[76px] flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive ? "text-gold" : "text-slate-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-link-admin"
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive ? "text-gold" : "text-slate-400 hover:text-white"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="nav-cta-book-call"
            className="flex items-center gap-2 bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] font-mono text-xs uppercase tracking-[0.15em] px-5 py-3 rounded-xl transition-all duration-200 shadow-[0_0_20px_rgba(93,123,255,0.35)]"
          >
            Book a Call
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#08090C]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `font-mono text-sm uppercase tracking-[0.2em] ${isActive ? "text-gold" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta-book-call"
                className="mt-2 bg-gold text-obsidian text-center font-mono text-xs uppercase tracking-[0.15em] px-5 py-3.5 rounded-xl"
              >
                Book a Call
              </Link>
              {user?.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-mobile-link-admin"
                  className="text-center font-mono text-xs uppercase tracking-[0.15em] px-5 py-3.5 rounded-xl bg-white/5 border border-white/15 text-white"
                >
                  Admin Dashboard
                </Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
